"use client";

import React from "react";
import { Button } from "../common/button";

export interface HowItWorksSimpleProps {
  title?: string;
  subtitle?: string;
  onGetStarted?: () => void;
  partnershipName?: string;
  sessionsCovered?: number | null;
  showPartnershipBenefit?: boolean;
}

export function HowItWorksSimple({
  title = "Start Therapy in 3 Simple Steps",
  subtitle = "Join 38,000+ Canadians who found their perfect therapist match through our personalized matching process.",
  onGetStarted,
  partnershipName,
  sessionsCovered,
  showPartnershipBenefit = false
}: HowItWorksSimpleProps) {
  const steps = [
    {
      number: "1",
      title: "Take a 2-minute quiz",
      description: "Tell us what you're looking for in under 2 minutes",
      details: showPartnershipBenefit && sessionsCovered && sessionsCovered > 0 ? [
        "Share what brings you to therapy",
        "Tell us about your preferences",
        "All answers are confidential"
      ] : [
        "Select your budget (from $45/session)",
        "Share what brings you to therapy",
        "All answers are confidential"
      ]
    },
    {
      number: "2", 
      title: "Get instant matches",
      description: "Receive 4 personalized therapist recommendations immediately",
      details: [
        "Matched to your specific needs",
        "View availability this week",
        "Compare approaches and specialties"
      ]
    },
    {
      number: "3",
      title: "Book your first session",
      description: "Schedule your first appointment with your chosen therapist",
      details: [
        "Book at a time that works for you",
        "Switch therapists anytime if needed",
        "Pause or cancel without fees"
      ]
    }
  ];

  const trustPoints = [
    {
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      text: "Covered by most insurance plans & benefits"
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
      text: "100% online - therapy from your safe space"
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      text: "Master's-level psychotherapists and social workers"
    }
  ];

  // Generate custom subtitle if partnership info is provided
  const displaySubtitle = showPartnershipBenefit && partnershipName && sessionsCovered && sessionsCovered > 0
    ? `Your first ${sessionsCovered} sessions are covered by ${partnershipName}. After that, you can continue with your therapist at a rate you select.`
    : subtitle;

  return (
    <section className="py-20 px-4 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {title}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {displaySubtitle}
          </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 mb-16 max-w-6xl mx-auto relative">
          {(() => {
            const bubbleColors = ["#dde3ed", "#fbe4d7", "#dde3ed"];
            return steps.map((step, index) => (
              <div key={index} className="relative text-center">
                <div className="flex flex-col items-center">
                  {/* Circle with number - same as YourJourney */}
                  <div 
                    className="w-16 h-16 rounded-full flex items-center justify-center mb-4 md:mb-6"
                    style={{ backgroundColor: bubbleColors[index] }}
                  >
                    <span className="text-2xl font-bold text-gray-700">{step.number}</span>
                  </div>
                  
                  {/* Content */}
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4">
                    {step.title}
                  </h3>
                  <p className="text-sm md:text-base text-gray-600 leading-relaxed max-w-xs mx-auto mb-6">
                    {step.description}
                  </p>
                  
                  {/* Details */}
                  <ul className="space-y-3 text-left max-w-xs mx-auto">
                    {step.details.map((detail, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                        <svg className="w-4 h-4 text-primary-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Dotted wave connector - same as YourJourney */}
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-5 left-[70%] w-[220px]">
                    <img 
                      src="https://cdn.prod.website-files.com/6110b06f10260e518f5d3077/6110b1396056a5e6b0bd01f9_Dot%20Wave.svg" 
                      alt="" 
                      className="w-full h-auto opacity-90"
                    />
                  </div>
                )}
              </div>
            ));
          })()}
        </div>

        {/* Trust points */}
        <div className="bg-gray-50 rounded-2xl p-8 mb-12">
          <div className="grid md:grid-cols-3 gap-6">
            {trustPoints.map((point, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <div className="text-primary-600">
                    {point.icon}
                  </div>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed pt-1">
                  {point.text}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <div className="inline-flex flex-col items-center gap-4">
            <Button 
              size="lg" 
              onClick={onGetStarted}
              className="bg-primary-600 hover:bg-primary-700"
            >
              Find Your Therapist Match
            </Button>
            <div className="flex items-center gap-6 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                </svg>
                <span>4.6/5 rating</span>
              </div>
              <span className="text-gray-400">•</span>
              <span>38,000+ Canadians helped</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}